import React from 'react'
import styles from './OrderForm.module.css'
import { OpenHour } from '../../types/Store'
import { getHourlySlots } from '../../helpers/getHourlySlots'
import Banner from '../Banner'
import { format, getDay } from 'date-fns'
import { getTodayStart, getTomorrowStart } from '../../helpers/dateDefaults'
import DatePicker from '../DatePicker'

interface Props {
  deliveryDate: string
  deliveryTimeSlot?: string
  deliveryTimeSlotError?: boolean
  dayOfWeek: string
  openHours: OpenHour[]
  calenderIsVisible: boolean
  onUpdateDeliveryDate: (date: Date) => void
  onUpdateDeliveryTimeSlot: (timeSlot: string) => void
  setDeliveryTimeSlotError?: (isError: boolean) => void
  paymentType?: string
  noBottomSpacing?: boolean
  bongCreatedOrder?: boolean
  headingText?: string
}

function Delivery ({ deliveryDate, openHours, paymentType, onUpdateDeliveryDate, onUpdateDeliveryTimeSlot, deliveryTimeSlot, deliveryTimeSlotError, setDeliveryTimeSlotError, dayOfWeek, noBottomSpacing = false, headingText = 'Ändra dag eller tid för leverans', bongCreatedOrder = false }: Props) {
  const [updatedDeliveryDate, setUpdatedDeliveryDate] = React.useState(new Date(deliveryDate))
  const [timeSlots, setTimeSlots] = React.useState<string[]>([])
  const [selectedTimeSlot, setSelectedTimeSlot] = React.useState<string | undefined>(deliveryTimeSlot)
  
  const today = getTodayStart()
  const tomorrow = getTomorrowStart()
  
  React.useEffect(() => {
    const dayNumber = getDay(updatedDeliveryDate)
    const deliveryTimeSlots = getHourlySlots(dayNumber, openHours)
    setTimeSlots(deliveryTimeSlots)
  }, [updatedDeliveryDate, openHours])

  const isDateAvailable = React.useCallback((date: Date) => {
    const dayNumber = getDay(date)
    if (!openHours || openHours.length === 0) return false
    const hoursForDay = openHours.filter((h) => h.day === dayNumber)
    return hoursForDay.some((h) => h.open !== h.close)
  }, [openHours])

  const handleDateSelected = (dateObj: Date) => {
    if (updatedDeliveryDate.getTime() !== dateObj.getTime()) {
      setUpdatedDeliveryDate(new Date(dateObj))
      onUpdateDeliveryDate(dateObj)
    }
  }

  const handleTimeSlotSelected = (timeSlot: string) => {
    setSelectedTimeSlot(timeSlot)
    onUpdateDeliveryTimeSlot(timeSlot)
    if (setDeliveryTimeSlotError) setDeliveryTimeSlotError(false)
  }

  return (
    <div className={styles.formSectionWithScroll}>
      <h4 className={styles.headingWithPadding}>{headingText}</h4>
      <div className={`${styles.deliveryContent} ${noBottomSpacing ? styles.noBottomSpacing : ''}`}>
        <p className={styles.deliveryLabel}>Välj leveransdatum</p>
        <Banner theme="info">
          <div className={styles.deliveryBanner}>
            <span>
              {deliveryTimeSlot
                ? 'Leveransdag och tid: '
                : 'Leveransdag: '}
              {dayOfWeek}, {format(updatedDeliveryDate, 'yyyy-MM-dd')}
              {deliveryTimeSlot ? `, ${selectedTimeSlot}` : ''}
            </span>
          </div>
        </Banner>
        <div className={styles.datePickerContainer}>
          <DatePicker 
            selectedDate={updatedDeliveryDate}
            onChange={(date) => { handleDateSelected(date)}}   
            inline={true}    
            minDate={paymentType === 'InStore' && !bongCreatedOrder ? today : tomorrow}
            filterDate={isDateAvailable}
          />
        </div>
        <p className={styles.deliveryLabel}>Välj tid för upphämtning</p>
        <div className={styles.timeSlotContainer} style={deliveryTimeSlotError ? { outline: '1px solid var(--redMid)' } : { border: 'none' }}>
          {openHours && timeSlots.map((timeSlot, i) => (
            <button
              key={i}
              className={timeSlot === selectedTimeSlot ? styles.timeSlotButtonActive : styles.timeSlotButton}
              onClick={() => handleTimeSlotSelected(timeSlot)}>
              {timeSlot}
            </button>
          ))}
        </div>
        {deliveryTimeSlotError && (
          <span className={styles.errorMessage}>Välj tid för upphämtning</span>
          
        )}
      </div>
    </div>
  )
}

export default Delivery
